import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { TeamMemberView, TeamView } from '../types';
import { getTeam } from '../utils/apiClient';
import { assignOutlineColors } from '../utils/avatarOutline';
import { useAuth } from './AuthContext';

interface TeamContextValue {
  team: TeamView | null;
  members: TeamMemberView[];
  byId: (userId: string | undefined | null) => TeamMemberView | null;
  self: TeamMemberView | null;
  /** Per-user outline ring color, unique within the team. */
  outlineByUserId: Record<string, string>;
  loading: boolean;
  error: string | null;
}

const Ctx = createContext<TeamContextValue | undefined>(undefined);

export function TeamProvider({ children }: PropsWithChildren<object>) {
  const { user } = useAuth();
  const [team, setTeam] = useState<TeamView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setTeam(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void getTeam()
      .then((t) => {
        if (!cancelled) {
          setTeam(t);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const value = useMemo<TeamContextValue>(() => {
    // ADR 0009: /team returns all access-groups the caller can see.
    // Flatten to a deduped member list so the viewer picker + avatar
    // resolution work across every group the caller belongs to.
    const seen = new Set<string>();
    const members: TeamMemberView[] = [];
    for (const g of team?.groups ?? []) {
      for (const m of g.members ?? []) {
        if (seen.has(m.userId)) continue;
        seen.add(m.userId);
        members.push(m);
      }
    }
    const outlineByUserId = assignOutlineColors(members);
    return {
      team,
      members,
      byId: (userId) => {
        if (!userId) return null;
        return members.find((m) => m.userId === userId) ?? null;
      },
      self: user ? members.find((m) => m.userId === user.userId) ?? null : null,
      outlineByUserId,
      loading,
      error,
    };
  }, [team, loading, error, user]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTeam(): TeamContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTeam must be used within a TeamProvider');
  return ctx;
}
